import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ArtistUrlParamsHook } from '~/modules/artist-roster/hooks/useUrlParams/types';
import { useUrlParams } from '~/modules/artist-roster/hooks/useUrlParams/useUrlParams';
import { areUrlParamsEqual } from '~/modules/artist-roster/hooks/useUrlParams/utils/areUrlParamsEqual';
import { PERSIST_SEARCH_PARAMS } from '~/modules/artist-roster/pages/artist-details/utils/constants';
import {
  formatFiltersToUrlParams,
  parseFiltersFromUrlParams,
} from '~/modules/artist-roster/pages/artist-details/utils/parsers/filters';
import type { TracksFilters } from '~/modules/artist-roster/pages/artist-details/utils/types';

/**
 * Hook to manage URL parameters for the tracks page.
 * {@link ArtistUrlParamsHook}
 */
export function useReleasesUrlParams(): ArtistUrlParamsHook<TracksFilters> {
  const [searchParams, setSearchParams] = useSearchParams();

  const properties = useUrlParams<TracksFilters>(
    formatFiltersToUrlParams,
    parseFiltersFromUrlParams,
  );

  // Override the setFilters method from useUrlParams to include persistence of search params.
  const setFilters = useCallback(
    (newFilters: Partial<TracksFilters>) => {
      const newFilterParams = formatFiltersToUrlParams(newFilters);
      const existingFilterUrlParams = formatFiltersToUrlParams(
        properties.filters,
      );

      if (areUrlParamsEqual(newFilterParams, existingFilterUrlParams)) {
        return;
      }

      for (const param of PERSIST_SEARCH_PARAMS) {
        const value = searchParams.get(param);

        if (value) {
          newFilterParams.set(param, value);
        }
      }

      setSearchParams(newFilterParams);
    },
    [properties.filters, searchParams, setSearchParams],
  );

  // Need to also override updateFilters here to make sure it calls setFilters
  // above which deals with PERSIST_SEARCH_PARAMS so that those don't get
  // overridden when the filters are updated.
  const updateFilters = useCallback(
    (filtersToUpdate: Partial<TracksFilters>) => {
      const mergedFilters = { ...properties.filters, ...filtersToUpdate };

      setFilters(mergedFilters);
    },
    [properties.filters, setFilters],
  );

  return {
    ...properties,
    setFilters,
    updateFilters,
  };
}
